// frontend/src/pages/AdminMonitoring.js
import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Alert, Badge, Button, Table, Spinner, Modal } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';
import api from '../api';
import MetricsViewer from '../components/MetricsViewer';
import '../styles/adminMonitoring.css';

// 注册 Chart.js 组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

const AdminMonitoring = () => {
  const [metrics, setMetrics] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(5000); // 5秒刷新
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [showMetricsViewer, setShowMetricsViewer] = useState(false);
  const [servicesStatus, setServicesStatus] = useState({
    prometheus: false,
    grafana: false
  });
  const intervalRef = useRef(null);

  // 获取监控数据
  const fetchMetrics = React.useCallback(async () => {
    try {
      const response = await fetch('/metrics');
      const metricsText = await response.text();
      const parsedMetrics = parsePrometheusMetrics(metricsText);
      setMetrics(parsedMetrics);
      
      // 添加到历史记录（保留最近20个数据点）
      setMetricsHistory(prev => {
        const newHistory = [...prev, {
          timestamp: new Date(),
          ...parsedMetrics
        }];
        return newHistory.slice(-20);
      });
      
      setError(null);
    } catch (err) {
      console.error('获取监控指标失败:', err);
      setError('无法获取监控数据，请检查后端服务是否正常运行');
    }
  }, []);

  // 获取健康状态
  const fetchHealthStatus = async () => {
    try {
      const response = await api.get('/health');
      setHealthStatus(response.data);
    } catch (err) {
      console.error('获取健康状态失败:', err);
      setHealthStatus({ status: 'unhealthy', error: err.message });
    }
  };

  // 解析 Prometheus 指标格式
  const parsePrometheusMetrics = (text) => {
    const lines = text.split('\n').filter(line => line && !line.startsWith('#'));
    const metrics = {};
    
    lines.forEach(line => {
      const match = line.match(/^([a-zA-Z_:][a-zA-Z0-9_:]*){?([^}]*)}?\s+([\d.e-]+)/);
      if (match) {
        const [, name, labels, value] = match;
        const labelObj = {};
        
        if (labels) {
          const labelPairs = labels.match(/(\w+)="([^"]*)"/g);
          if (labelPairs) {
            labelPairs.forEach(pair => {
              const [key, val] = pair.split('=');
              labelObj[key] = val.replace(/"/g, '');
            });
          }
        }
        
        if (!metrics[name]) metrics[name] = [];
        metrics[name].push({ labels: labelObj, value: parseFloat(value) });
      }
    });
    
    return metrics;
  };

  // 获取指标值
  const getMetricValue = (metricName, labelFilter = {}) => {
    if (!metrics || !metrics[metricName]) return 0;
    
    const metric = metrics[metricName].find(m => {
      return Object.keys(labelFilter).every(key => 
        m.labels[key] === labelFilter[key]
      );
    });
    
    return metric ? metric.value : 0;
  };

  // 获取指标总和
  const getMetricSum = (metricName) => {
    if (!metrics || !metrics[metricName]) return 0;
    return metrics[metricName].reduce((sum, m) => sum + m.value, 0);
  };

  // 检测外部服务状态
  const checkExternalServices = async () => {
    // 检查 Prometheus 是否可访问
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      await fetch('http://localhost:9090/api/v1/query?query=up', {
        signal: controller.signal,
        mode: 'no-cors' // 绕过CORS限制
      });
      
      clearTimeout(timeoutId);
      setServicesStatus(prev => ({ ...prev, prometheus: true }));
    } catch (error) {
      console.log('Prometheus服务检测失败:', error.message);
      setServicesStatus(prev => ({ ...prev, prometheus: false }));
    }

    // 检查 Grafana 是否可访问
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      await fetch('http://localhost:3001/api/health', {
        signal: controller.signal,
        mode: 'no-cors' // 绕过CORS限制
      });
      
      clearTimeout(timeoutId);
      setServicesStatus(prev => ({ ...prev, grafana: true }));
    } catch (error) {
      console.log('Grafana服务检测失败:', error.message);
      setServicesStatus(prev => ({ ...prev, grafana: false }));
    }
  };

  // 组件挂载时开始获取数据
  useEffect(() => {
    fetchMetrics();
    fetchHealthStatus();
    checkExternalServices();
    setLoading(false);
  }, [fetchMetrics]);

  // 自动刷新逻辑
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchMetrics();
        fetchHealthStatus();
      }, refreshInterval);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchMetrics]);

  // 处理Grafana按钮点击
  const handleGrafanaClick = () => {
    // 无论服务状态如何，都允许尝试打开
    const grafanaUrl = 'http://localhost:3001';
    const newWindow = window.open(grafanaUrl, '_blank');
    
    if (!newWindow) {
      alert('无法打开新窗口！\n\n请检查浏览器是否阻止弹窗，或者手动访问：\n' + grafanaUrl);
    } else if (!servicesStatus.grafana) {
      // 如果服务状态显示未部署，给出提示
      setTimeout(() => {
        alert('Grafana服务可能未部署或不可访问！\n\n如果页面无法加载，请先运行以下命令部署Grafana：\n\ndocker run -d --name grafana -p 3001:3000 grafana/grafana\n\n然后访问 http://localhost:3001\n默认用户名/密码：admin/admin');
      }, 100);
    }
  };

  // 处理Prometheus按钮点击
  const handlePrometheusClick = () => {
    // 无论服务状态如何，都允许尝试打开
    const prometheusUrl = 'http://localhost:9090';
    const newWindow = window.open(prometheusUrl, '_blank');
    
    if (!newWindow) {
      alert('无法打开新窗口！\n\n请检查浏览器是否阻止弹窗，或者手动访问：\n' + prometheusUrl);
    } else if (!servicesStatus.prometheus) {
      // 如果服务状态显示未部署，给出提示
      setTimeout(() => {
        alert('Prometheus服务可能未部署或不可访问！\n\n如果页面无法加载，请先运行以下命令部署Prometheus：\n\ndocker run -d --name prometheus -p 9090:9090 prom/prometheus\n\n然后访问 http://localhost:9090');
      }, 100);
    }
  };

  // 手动刷新
  const handleRefresh = () => {
    setLoading(true);
    Promise.all([fetchMetrics(), fetchHealthStatus()]).finally(() => {
      setLoading(false);
    });
  };

  // 准备图表数据
  const prepareChartData = () => {
    if (metricsHistory.length === 0) return null;

    const labels = metricsHistory.map(h => 
      h.timestamp.toLocaleTimeString()
    );

    return {
      // 活跃用户数趋势
      activeUsersChart: {
        labels,
        datasets: [{
          label: '活跃用户数',
          data: metricsHistory.map(h => h.active_users_gauge?.[0]?.value || 0),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.4
        }]
      },
      
      // 设备在线率趋势
      deviceOnlineChart: {
        labels,
        datasets: [{
          label: '设备在线率 (%)',
          data: metricsHistory.map(h => (h.device_online_rate_gauge?.[0]?.value || 0) * 100),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.4
        }]
      },

      // 内存使用趋势
      memoryChart: {
        labels,
        datasets: [{
          label: '内存使用率 (%)',
          data: metricsHistory.map(h => (h.system_memory_usage_ratio?.[0]?.value || 0) * 100),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.4
        }]
      }
    };
  };

  const chartData = prepareChartData();

  // 获取下分成功率
  const getDebitSuccessRate = () => {
    if (!metrics) return 0;
    const successCount = getMetricValue('debit_operations_total', { status: 'success' });
    const failCount = getMetricValue('debit_operations_total', { status: 'fail' });
    const total = successCount + failCount;
    return total > 0 ? ((successCount / total) * 100).toFixed(1) : 100;
  };

  // 图表选项
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading && !metrics) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">加载监控数据中...</span>
      </div>
    );
  }

  return (
    <div className="admin-monitoring">
      <div className="monitoring-header">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>系统监控 & 可观测性</h2>
          <div className="monitoring-controls">
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={handleRefresh}
              disabled={loading}
              className="me-2"
            >
              {loading ? <Spinner size="sm" animation="border" /> : '🔄'} 刷新
            </Button>
            <Button 
              variant="outline-secondary"
              size="sm"
              onClick={checkExternalServices}
              className="me-2"
              title="重新检测外部监控服务状态"
            >
              🔍 检测服务
            </Button>
            <Button 
              variant={autoRefresh ? "success" : "outline-secondary"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? '⏸️ 暂停自动刷新' : '▶️ 开始自动刷新'}
            </Button>
          </div>
        </div>

        {/* 外部监控服务状态 */}
        {(!servicesStatus.prometheus || !servicesStatus.grafana) && (
          <Alert variant="warning" className="mb-4">
            <Alert.Heading>⚠️ 外部监控服务未部署</Alert.Heading>
            <p>
              检测到以下监控服务未部署或无法访问：
            </p>
            <ul className="mb-3">
              {!servicesStatus.prometheus && <li>❌ Prometheus (localhost:9090) - 指标收集和存储</li>}
              {!servicesStatus.grafana && <li>❌ Grafana (localhost:3001) - 可视化仪表盘</li>}
            </ul>
            <hr />
            <div className="mb-3">
              <strong>📋 快速部署步骤：</strong>
              <ol className="mt-2">
                <li>安装 Docker Desktop：<a href="https://www.docker.com/products/docker-desktop/" target="_blank" rel="noopener noreferrer">下载链接</a></li>
                <li>运行部署命令（在终端中执行）：
                  <br />
                  <code className="d-block mt-1 p-2 bg-dark text-light">
                    # 部署 Prometheus<br />
                    docker run -d --name prometheus -p 9090:9090 prom/prometheus<br /><br />
                    # 部署 Grafana<br />
                    docker run -d --name grafana -p 3001:3000 grafana/grafana
                  </code>
                </li>
                <li>等待服务启动完成（约1-2分钟）</li>
                <li>点击上方按钮重新检测服务状态</li>
              </ol>
            </div>
            <p className="mb-0">
              <strong>💡 提示：</strong>部署完成后，Grafana默认用户名/密码为 admin/admin，首次登录会要求修改密码。
            </p>
          </Alert>
        )}

        {/* 健康状态概览 */}
        <Alert variant={healthStatus?.status === 'healthy' ? 'success' : 'danger'} className="mb-4">
          <div className="d-flex align-items-center">
            <span className="me-2">
              {healthStatus?.status === 'healthy' ? '✅' : '❌'}
            </span>
            <strong>系统健康状态: </strong>
            <Badge bg={healthStatus?.status === 'healthy' ? 'success' : 'danger'} className="ms-2">
              {healthStatus?.status === 'healthy' ? '正常' : '异常'}
            </Badge>
            {healthStatus?.error && (
              <span className="ms-2 text-muted">({healthStatus.error})</span>
            )}
          </div>
        </Alert>

        {error && (
          <Alert variant="warning" className="mb-4">
            ⚠️ {error}
          </Alert>
        )}
      </div>

      {/* 关键指标卡片 */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="metric-card bg-primary text-white">
            <Card.Body>
              <div className="metric-icon">👥</div>
              <div className="metric-content">
                <h3>{getMetricValue('active_users_gauge').toFixed(0)}</h3>
                <p>活跃用户数</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card bg-success text-white">
            <Card.Body>
              <div className="metric-icon">🎮</div>
              <div className="metric-content">
                <h3>{(getMetricValue('device_online_rate_gauge') * 100).toFixed(1)}%</h3>
                <p>设备在线率</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card bg-info text-white">
            <Card.Body>
              <div className="metric-icon">💾</div>
              <div className="metric-content">
                <h3>{(getMetricValue('system_memory_usage_ratio') * 100).toFixed(1)}%</h3>
                <p>内存使用率</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card bg-warning text-white">
            <Card.Body>
              <div className="metric-icon">💰</div>
              <div className="metric-content">
                <h3>{getDebitSuccessRate()}%</h3>
                <p>下分成功率</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 趋势图表 */}
      {chartData && (
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>活跃用户数趋势</Card.Header>
              <Card.Body>
                <div style={{ height: '300px' }}>
                  <Line data={chartData.activeUsersChart} options={chartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>设备在线率趋势</Card.Header>
              <Card.Body>
                <div style={{ height: '300px' }}>
                  <Line data={chartData.deviceOnlineChart} options={chartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* 业务指标详情 */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>业务指标统计</Card.Header>
            <Card.Body>
              <Table striped bordered hover size="sm">
                <tbody>
                  <tr>
                    <td>下分成功次数</td>
                    <td className="text-end text-success">
                      {getMetricSum('debit_operations_total')}
                    </td>
                  </tr>
                  <tr>
                    <td>设备释放次数</td>
                    <td className="text-end">
                      {getMetricSum('device_release_total')}
                    </td>
                  </tr>
                  <tr>
                    <td>HTTP请求总数</td>
                    <td className="text-end">
                      {getMetricSum('http_requests_total')}
                    </td>
                  </tr>
                  <tr>
                    <td>Socket连接数</td>
                    <td className="text-end">
                      {getMetricValue('socket_connections_gauge')}
                    </td>
                  </tr>
                  <tr>
                    <td>MongoDB连接数</td>
                    <td className="text-end">
                      {getMetricValue('mongodb_connections_gauge')}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>系统资源监控</Card.Header>
            <Card.Body>
              <div style={{ height: '250px' }}>
                {chartData && (
                  <Line data={chartData.memoryChart} options={chartOptions} />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 快捷操作 */}
      <Row>
        <Col md={12}>
          <Card>
            <Card.Header>监控工具 & 链接</Card.Header>
            <Card.Body>
              <div className="d-flex gap-3">
                <Button 
                  variant="outline-primary" 
                  onClick={() => window.open('/metrics', '_blank')}
                >
                  📊 查看原始指标
                </Button>
                <Button 
                  variant="outline-success" 
                  onClick={() => window.open('/health', '_blank')}
                >
                  ❤️ 健康检查
                </Button>
                <Button 
                  variant="info" 
                  onClick={() => setShowMetricsViewer(true)}
                >
                  📋 友好指标查看器
                </Button>
                <Button 
                  variant={servicesStatus.grafana ? "outline-info" : "outline-danger"}
                  onClick={handleGrafanaClick}
                  title={servicesStatus.grafana ? "打开 Grafana 仪表盘 (localhost:3001)" : "Grafana 未部署，点击查看部署指导"}
                >
                  📈 Grafana 仪表盘
                  {servicesStatus.grafana ? (
                    <Badge bg="success" className="ms-1">●</Badge>
                  ) : (
                    <Badge bg="danger" className="ms-1">✗</Badge>
                  )}
                </Button>
                <Button 
                  variant={servicesStatus.prometheus ? "outline-warning" : "outline-danger"}
                  onClick={handlePrometheusClick}
                  title={servicesStatus.prometheus ? "打开 Prometheus 监控 (localhost:9090)" : "Prometheus 未部署，点击查看部署指导"}
                >
                  🔥 Prometheus 监控
                  {servicesStatus.prometheus ? (
                    <Badge bg="success" className="ms-1">●</Badge>
                  ) : (
                    <Badge bg="danger" className="ms-1">✗</Badge>
                  )}
                </Button>
              </div>
              <small className="text-muted mt-2 d-block">
                💡 提示：点击按钮将打开外部监控服务。如果无法访问，请按照 MONITORING_GUIDE.md 部署 Prometheus (localhost:9090) 和 Grafana (localhost:3001)
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 友好指标查看器弹窗 */}
      <Modal 
        show={showMetricsViewer} 
        onHide={() => setShowMetricsViewer(false)} 
        size="xl"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>📊 系统指标详情</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <MetricsViewer />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminMonitoring;
